import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  COOKIE_CATEGORIES,
  DEFAULT_COOKIE_CONSENT,
  readCookieConsent,
  saveCookieConsent,
} from "../utils/cookieConsent";

const CookieBanner = () => {
  const [consent, setConsent] = useState(DEFAULT_COOKIE_CONSENT);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const storedConsent = readCookieConsent();
    setConsent(storedConsent);
    setPreferences({
      analytics: Boolean(storedConsent.categories?.analytics),
      marketing: Boolean(storedConsent.categories?.marketing),
    });
  }, []);

  const shouldShowBanner = useMemo(
    () => consent.status === "pending",
    [consent.status]
  );

  if (!shouldShowBanner) return null;

  const persistConsent = (status, categories) => {
    const saved = saveCookieConsent({
      status,
      categories: {
        [COOKIE_CATEGORIES.ESSENTIAL]: true,
        [COOKIE_CATEGORIES.ANALYTICS]: Boolean(categories.analytics),
        [COOKIE_CATEGORIES.MARKETING]: Boolean(categories.marketing),
      },
    });
    setConsent(saved);
  };

  const handleAcceptAll = () => {
    persistConsent("accepted", { analytics: true, marketing: true });
  };

  const handleRejectOptional = () => {
    persistConsent("rejected", { analytics: false, marketing: false });
  };

  const handleSavePreferences = () => {
    persistConsent("customized", preferences);
  };

  return (
    <Box
      role="dialog"
      aria-label="Configuración de cookies"
      sx={{
        position: "fixed",
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 1400,
        backgroundColor: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: "12px",
        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.18)",
        p: { xs: 2, md: 3 },
        maxWidth: "980px",
        mx: "auto",
      }}
    >
      <Stack spacing={1.5}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
          Preferencias de privacidad y cookies
        </Typography>
        <Typography variant="body2" sx={{ color: "#444" }}>
          Usamos cookies esenciales para que el sitio funcione, y opcionales para
          analitica y marketing. Puedes aceptar todas, rechazar las opcionales o
          configurarlas. Consulta la{" "}
          <Link component={RouterLink} to="/cookie-policy" underline="hover">
            Politica de Cookies
          </Link>{" "}
          y la{" "}
          <Link component={RouterLink} to="/privacy-policy" underline="hover">
            Politica de Privacidad
          </Link>
          .
        </Typography>

        <Collapse in={showPreferences}>
          <Stack spacing={0.5} sx={{ py: 1 }}>
            <FormControlLabel
              control={<Switch checked disabled />}
              label="Cookies esenciales (siempre activas)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.analytics}
                  onChange={(event) =>
                    setPreferences((prev) => ({
                      ...prev,
                      analytics: event.target.checked,
                    }))
                  }
                />
              }
              label="Cookies de analitica"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.marketing}
                  onChange={(event) =>
                    setPreferences((prev) => ({
                      ...prev,
                      marketing: event.target.checked,
                    }))
                  }
                />
              }
              label="Cookies de marketing"
            />
          </Stack>
        </Collapse>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button variant="contained" onClick={handleAcceptAll}>
            Aceptar todas
          </Button>
          <Button variant="outlined" onClick={handleRejectOptional}>
            Rechazar opcionales
          </Button>
          <Button
            variant="text"
            onClick={() => setShowPreferences((prev) => !prev)}
          >
            {showPreferences ? "Ocultar preferencias" : "Personalizar"}
          </Button>
          {showPreferences && (
            <Button variant="text" onClick={handleSavePreferences}>
              Guardar seleccion
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default CookieBanner;

